import React, {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  Dispatch,
  useCallback,
} from 'react';

interface _FeatureContextData<T> {
  data: T;
  dispatch: Dispatch<Action<T>>;
  initialData?: T;
}

type Action<T> = { type: 'UPDATE_DATA'; payload: T };

function _featureReducer<T>(state: T, action: Action<T>): T {
  switch (action.type) {
    case 'UPDATE_DATA':
      return action.payload;
    default:
      return state;
  }
}

interface _ProviderProps<T> {
  children: ReactNode;
}

export function createFeatureContext<T>(initialData) {
  const FeatureContext = createContext<_FeatureContextData<T> | undefined>(
    initialData,
  );

  const FeatureContextProvider: React.FC<_ProviderProps<T>> = ({
    children,
  }) => {
    const [data, dispatch] = useReducer(
      _featureReducer,
      initialData || ({} as T),
    );

    return (
      <FeatureContext.Provider
        value={{
          data: data as T,
          dispatch,
          initialData,
        }}
      >
        {children}
      </FeatureContext.Provider>
    );
  };

  const useFeatureContext = () => {
    const context = useContext(FeatureContext);
    if (context === undefined) {
      throw new Error(
        'useFeatureContext debe ser utilizado dentro de un FeatureContextProvider',
      );
    }
    const updateState = useCallback((payload: T) => {
      context.dispatch({
        type: 'UPDATE_DATA',
        payload: { ...payload },
      });
    }, []);
    const clearState = useCallback(() => {
      context.dispatch({
        type: 'UPDATE_DATA',
        payload: context.initialData as T,
      });
    }, []);
    return {
      state: context.data,
      updateState,
      clearState,
    };
  };

  return { FeatureContextProvider, useFeatureContext };
}
